<template>
    <div
        class="flex w-full"
        :class="{}"
        @dragenter="dragEnter"
        @dragleave="dragLeave"
        @dragover="dragOver"
        @dragend="dragEnd"
    >
        <div class="pt-5 pr-1 shrink-0">
            <vx-button color="transparent" size="sm" type="button" class="cursor-move! section-handle">
                <font-awesome-icon widthAuto :icon="faGripDotsVertical"></font-awesome-icon>
            </vx-button>
        </div>
        <div
            class="px-6 py-4 rounded-2xl border-b-solid border-b border-white flex grow flex-col transition-all"
            :class="{
                'bg-slate-300/30 shadow-inner': props.open,
                'bg-white shadow': !props.open,
            }"
        >
            <div class="flex items-center justify-between gap-4">
                <div class="flex items-center text-slate-800">
                    <font-awesome-icon widthAuto :icon="faBookmark"></font-awesome-icon>
                    <div class="ml-3 mr-4 font-extrabold">{{ props.section.name }}</div>
                    <!-- hard coded name until renaming is enabled -->
                    <vx-badge color="slate" size="sm">{{ props.section.resources?.length || 0 }}</vx-badge>
                </div>
                <div class="grow">
                    <div
                        class="h-0.5 bg-amber-400 rounded w-full opacity-0 transition"
                        :class="{
                            'opacity-100':
                                over &&
                                !props.open &&
                                resourceDragging.oldSectionIndex != null &&
                                resourceDragging.oldSectionIndex !== props.flowSection,
                        }"
                    ></div>
                </div>
                <div class="flex gap-2">
                    <vx-dropdown>
                        <vx-button type="button" size="md" outline color="muted-dark">
                            <font-awesome-icon widthAuto :icon="faEllipsis"></font-awesome-icon>
                        </vx-button>

                        <template v-slot:items>
                            <vx-dropdown-item @click.prevent="edit = true" type="button">
                                Edit <font-awesome-icon widthAuto :icon="faPenField"></font-awesome-icon>
                            </vx-dropdown-item>
                            <vx-popover class="w-full" hover :arrow="true" placement="top">
                                <vx-dropdown-item
                                    @click.prevent="removeSection"
                                    :disabled="sections.length === 1"
                                    type="button"
                                    data-test="remove-section"
                                >
                                    Delete <font-awesome-icon widthAuto :icon="faTrashCan"></font-awesome-icon>
                                </vx-dropdown-item>
                                <template v-if="sections.length === 1" v-slot:content>
                                    At least one section is required.
                                </template>
                            </vx-popover>
                        </template>
                    </vx-dropdown>
                    <vx-button
                        @click="emit('update:open', !props.open)"
                        data-test="open-resources"
                        type="button"
                        size="md"
                        color="muted-dark"
                    >
                        <font-awesome-icon
                            widthAuto
                            :icon="props.open ? faChevronUp : faChevronDown"
                        ></font-awesome-icon>
                    </vx-button>
                </div>
            </div>

            <vx-alert v-if="validStopAt" color="info" :closeable="false" shadow class="my-4">
                <template v-slot:icon>
                    <font-awesome-icon :icon="faDiamondExclamation" widthAuto></font-awesome-icon>
                </template>
                <strong>Scheduled Stop: </strong> {{ validStopAt }}
            </vx-alert>

            <div v-if="!props.open && props.section.resources.length" class="mt-4 flex items-center">
                <div
                    v-for="(resource, resourceIndex) in props.section.resources"
                    :key="'bar.' + resource.type + '.' + resource.id"
                    :style="{ width: `calc(${(1 / props.section.resources.length) * 100}% + 4px)` }"
                    class="h-3 border-2 border-white border-solid rounded-sm"
                    :class="{
                        'bg-amber-300': resource.type === 'automation',
                        'bg-sky-300': resource.type === 'campaign',
                        'bg-violet-300': resource.type === 'segment',
                        '-ml-1': resourceIndex !== 0,
                    }"
                ></div>
            </div>

            <animate-height :height="props.open ? 'auto' : 0">
                <div class="mt-4 space-y-4 pr-2">
                    <div ref="resourcesListRef" class="space-y-4">
                        <flow-resource
                            v-for="resource in props.section.resources"
                            :key="resource.type + '.' + resource.id"
                            :resource="resource"
                            :flowSection="props.flowSection"
                            @remove="removeResources = [resource]"
                        ></flow-resource>
                        <div
                            v-if="!props.section.resources.length"
                            class="sortable-ignore text-black pt-11 px-4 bg-slate-200 rounded-2xl text-center border border-slate-400 justify-between items-center flex-col flex border-dashed gap-4"
                        >
                            <font-awesome-icon
                                widthAuto
                                class="text-2xl"
                                :icon="faSquareDashedCirclePlus"
                            ></font-awesome-icon>
                            <div>Drag items to this section.</div>
                            <div
                                class="mb-6 mt-2 h-0.5 bg-amber-400 rounded w-full opacity-0 transition"
                                :class="{
                                    'opacity-100':
                                        over &&
                                        resourceDragging.oldSectionIndex != null &&
                                        resourceDragging.oldSectionIndex !== props.flowSection,
                                }"
                            ></div>
                        </div>
                    </div>

                    <div
                        class="pl-6 pr-4 py-4 bg-slate-200 rounded-2xl border border-slate-400 justify-between items-center flex-col flex border-dashed gap-2 lg:flex-row"
                    >
                        <div class="text-black">Add a resource:</div>
                        <div class="flex gap-2">
                            <vx-button @click="addResource = true" type="button" color="muted-dark">
                                Existing
                                <font-awesome-icon widthAuto :icon="faMagnifyingGlass"></font-awesome-icon>
                            </vx-button>
                            <vx-dropdown>
                                <vx-button type="button" color="secondary">
                                    New
                                    <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                                </vx-button>
                                <template v-slot:items>
                                    <vx-dropdown-item
                                        :href="
                                            '/contacts/segments/new?flow_id=' +
                                            props.flowId +
                                            '&flow_section=' +
                                            props.flowSection
                                        "
                                        data-test="add-segment"
                                    >
                                        Segment <font-awesome-icon widthAuto :icon="faContactBook"></font-awesome-icon>
                                    </vx-dropdown-item>
                                    <vx-dropdown-item
                                        :href="
                                            '/messaging/campaigns/new?flow_id=' +
                                            props.flowId +
                                            '&flow_section=' +
                                            props.flowSection
                                        "
                                        data-test="add-campaign"
                                    >
                                        Campaign <font-awesome-icon widthAuto :icon="faMessages"></font-awesome-icon>
                                    </vx-dropdown-item>
                                    <vx-dropdown-item
                                        :href="
                                            '/automation/event/new?flow_id=' +
                                            props.flowId +
                                            '&flow_section=' +
                                            props.flowSection
                                        "
                                        data-test="add-automations"
                                    >
                                        Automations <font-awesome-icon widthAuto :icon="faRobot"></font-awesome-icon>
                                    </vx-dropdown-item>
                                </template>
                            </vx-dropdown>
                        </div>
                    </div>
                </div>
            </animate-height>
        </div>

        <flow-resource-add v-model:visible="addResource" :flowSection="props.flowSection"></flow-resource-add>
        <flow-resource-remove
            :visible="!!removeResources.length"
            @update:visible="removeResources = []"
            @removed="
                () => {
                    if (removingSection) {
                        removingSection = false;
                        emit('remove');
                    }
                }
            "
            :resources="removeResources"
        ></flow-resource-remove>

        <flow-section-form
            v-if="sections[props.flowSection]"
            v-model:visible="edit"
            :modelValue="sectionData"
        ></flow-section-form>
    </div>
</template>

<script setup>
import {
    faBookmark,
    faChevronDown,
    faChevronUp,
    faCirclePlus,
    faContactBook,
    faDiamondExclamation,
    faEllipsis,
    faGripDotsVertical,
    faMagnifyingGlass,
    faMessages,
    faPenField,
    faRobot,
    faSquareDashedCirclePlus,
    faTrashCan,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxAlert, VxBadge, VxButton, VxDropdown, VxDropdownItem, VxPopover } from '@voxie/frontend-components';
import { computed, nextTick, ref, watch } from 'vue';
import AnimateHeight from 'vue-animate-height';
import { moveArrayElement, useSortable } from '@vueuse/integrations/useSortable';
import { useToasted } from '~/composables/useToasted';
import { HTTP_INTERNAL_SERVER_ERROR } from '~/support/HttpStatuses';
import FlowResource from './FlowResource.vue';
import FlowResourceRemove from './FlowResourceRemove.vue';
import FlowSectionForm from './FlowSectionForm.vue';
import FlowResourceAdd from './flow-resource-add/FlowResourceAdd.vue';
import { useFlow } from './useFlow';
import dayjs from '~/utils/dayjs';
import { formatTz, getClientTz } from '~/utils/date';

const props = defineProps({
    open: {
        type: Boolean,
        default: false,
    },
    flowId: {
        type: String,
        required: true,
    },
    section: {
        type: Object,
        required: true,
    },
    flowSection: {
        type: Number,
        required: true,
    },
});

const toasted = useToasted();

const emit = defineEmits(['remove', 'update:open', 'draggable:change']);

const edit = ref(false);

const over = ref(false);
const overCounter = ref(0);
const { resourceDragging, saveSections, sections } = useFlow();

const resourcesListRef = ref(null);

useSortable(resourcesListRef, () => props.section.resources, {
    handle: '.resource-handle',
    animation: 200,
    group: { name: 'resources' },
    filter: '.sortable-ignore',
    ghostClass: 'opacity-30',
    dragClass: 'resource-dragging',
    onUpdate: (evt) => {
        moveArrayElement(() => props.section.resources, evt.oldIndex, evt.newIndex, evt);

        nextTick(() => {
            emit('draggable:change', evt);
        });
    },
    onAdd: (evt) => {
        if (evt.pullMode === 'clone') {
            return;
        }

        const sourceIdx = resourceDragging.oldSectionIndex;
        const destIdx = props.flowSection;

        if (sourceIdx == null || sourceIdx === destIdx) {
            return;
        }

        const srcSection = sections.value[sourceIdx];
        const destSection = sections.value[destIdx];

        if (!srcSection || !destSection) {
            return;
        }

        const resource = srcSection.resources[evt.oldIndex];

        if (!resource) {
            return;
        }

        // Revert Sortable's cross-list DOM move so Vue's reactive re-render is authoritative.
        evt.from.insertBefore(evt.item, evt.from.children[evt.oldIndex] ?? null);

        nextTick(() => {
            if (!sections.value[sourceIdx] || !sections.value[destIdx]) {
                return;
            }

            sections.value[sourceIdx].resources.splice(evt.oldIndex, 1);
            sections.value[destIdx].resources.splice(evt.newIndex, 0, resource);
            emit('draggable:change', evt);
        });
    },
});

const sectionData = computed(() => ({ name: props.section.name, stopAt: props.section.stop_at || undefined }));

const validStopAt = computed(() => {
    if (!props.section.stop_at) {
        return null;
    }

    return (
        dayjs(props.section.stop_at).tz(getClientTz()).format('MMM DD, YYYY [at] h:mm a') +
        ' ' +
        formatTz(dayjs().tz(getClientTz()).format('z'))
    );
});

// Custom drag handling for a resource between sections.
watch(
    () => resourceDragging.oldSectionIndex,
    (oldSectionIndex) => {
        if (isNaN(oldSectionIndex)) {
            over.value = false;
            overCounter.value = 0;
        }
    }
);

const dragEnter = () => {
    over.value = true;
    overCounter.value++;
};

const dragLeave = () => {
    overCounter.value--;

    if (overCounter.value <= 0) {
        over.value = false;
    }
};

const dragOver = () => {
    resourceDragging.newSectionIndex = props.flowSection;
};

const dragEnd = async () => {
    const resource = resourceDragging.resource;
    const oldSectionIndex = resourceDragging.oldSectionIndex;
    const newSectionIndex = resourceDragging.newSectionIndex;

    if (!resource || oldSectionIndex === newSectionIndex) {
        return;
    }

    // Sortable handles open-destination drops via onAdd; this fallback only runs for collapsed targets.
    if (sections.value[newSectionIndex]?.open) {
        return;
    }

    if (!sections.value[oldSectionIndex] || !sections.value[newSectionIndex]) {
        return;
    }

    sections.value[oldSectionIndex].resources = sections.value[oldSectionIndex].resources.filter(
        (existing) => !(existing.type === resource.type && existing.id === resource.id)
    );
    sections.value[newSectionIndex].resources.push(resource);

    try {
        await saveSections(sections.value);
    } catch (e) {
        console.error(e);
        if (!e?.response?.status || e.response.status >= HTTP_INTERNAL_SERVER_ERROR) {
            toasted.global.platform_error();
        } else {
            toasted.error(e.response?.data?.message || 'Something went wrong.');
        }
    }
};

const addResource = ref(false);
const removeResources = ref([]);

const removingSection = ref(false);
const removeSection = async () => {
    if (props.section.resources.length) {
        removingSection.value = true;
        removeResources.value = [...props.section.resources];
    } else {
        emit('remove');
    }
};
</script>
